import type { LocationPublic } from '@repo/types/locations';
import type { FieldPath, FieldValues, PathValue, UseFormReturn } from 'react-hook-form';

import { cn } from '@repo/lib/cn';
import { CheckIcon, ChevronsUpDownIcon, ClipboardPasteIcon, MapPinIcon } from 'lucide-react';
import { useId, useRef } from 'react';
import { useWatch } from 'react-hook-form';

import { Button } from '@/components/ui/button';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { FormControl, FormField, FormItem, FormMessage } from '@/components/ui/form';
import { InputGroup, InputGroupAddon, InputGroupButton, InputGroupInput } from '@/components/ui/input-group';
import { Label } from '@/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';

interface MeetingTypeFormFieldProps<
  TFieldValues extends FieldValues,
  TTypeName extends FieldPath<TFieldValues>,
  TMeetingURLName extends FieldPath<TFieldValues>,
  TLocationName extends FieldPath<TFieldValues>,
> {
  form: UseFormReturn<TFieldValues>;
  typeName: TTypeName;
  meetingURLName: TMeetingURLName;
  locationName: TLocationName;
  locations: LocationPublic[];
  isLocationsPending: boolean;
}

function isMeetingTypeValue(value: string): value is 'remote' | 'stationary' {
  return value === 'remote' || value === 'stationary';
}

export function MeetingTypeFormField<
  TFieldValues extends FieldValues,
  TTypeName extends FieldPath<TFieldValues>,
  TMeetingURLName extends FieldPath<TFieldValues>,
  TLocationName extends FieldPath<TFieldValues>,
>(meetingTypeFormFieldProps: MeetingTypeFormFieldProps<TFieldValues, TTypeName, TMeetingURLName, TLocationName>) {
  const { form, typeName, meetingURLName, locationName, locations, isLocationsPending } = meetingTypeFormFieldProps;

  const radioId = useId();
  const remoteId = useId();
  const stationaryId = useId();
  const inputMeetingURLRef = useRef<HTMLInputElement>(null);
  const type = useWatch({ control: form.control, name: typeName });

  return (
    <FormField
      control={form.control}
      name={typeName}
      render={({ field }) => (
        <FormItem className="space-y-3">
          <FormControl>
            <RadioGroup
              className={'gap-3'}
              value={field.value ?? 'remote'}
              onValueChange={(value) => {
                if (!isMeetingTypeValue(value)) return;
                field.onChange(value);
                if (value === 'remote') {
                  form.setValue(locationName, null as PathValue<TFieldValues, TLocationName>);
                }
                if (value === 'stationary') {
                  form.setValue(meetingURLName, null as PathValue<TFieldValues, TMeetingURLName>);
                }
                if (value === 'remote' && inputMeetingURLRef.current) {
                  setTimeout(() => {
                    inputMeetingURLRef.current?.focus();
                  }, 0);
                }
              }}
            >
              <div>
                <div className="flex items-start gap-2">
                  <RadioGroupItem value="remote" id={`${radioId}-1`} aria-describedby={`${radioId}-1-description`} aria-controls={remoteId} />
                  <div className="grow">
                    <div className="grid grow gap-2">
                      <Label htmlFor={`${radioId}-1`}>Zdalnie</Label>
                      <p id={`${radioId}-1-description`} className="text-muted-foreground text-xs">
                        Wybierz jeżeli chcesz aby spotkanie odbyło się online.
                      </p>
                    </div>
                    <div
                      role="region"
                      id={remoteId}
                      aria-labelledby={`${radioId}-1`}
                      className="grid transition-all ease-in-out data-[state=collapsed]:grid-rows-[0fr] data-[state=collapsed]:opacity-0 data-[state=expanded]:grid-rows-[1fr] data-[state=expanded]:opacity-100"
                      data-state={type === 'remote' ? 'expanded' : 'collapsed'}
                    >
                      <div className="pointer-events-none -m-2 overflow-hidden p-2">
                        <div className="pointer-events-auto mt-3">
                          <FormField
                            name={meetingURLName}
                            control={form.control}
                            render={({ field }) => (
                              <FormItem>
                                <FormControl>
                                  <InputGroup>
                                    <InputGroupInput
                                      {...field}
                                      ref={inputMeetingURLRef}
                                      value={field.value ?? ''}
                                      onChange={(event) => {
                                        const value = event.target.value;
                                        field.onChange(value ? value : null);
                                      }}
                                      disabled={type !== 'remote'}
                                      placeholder={'Link do spotkania'}
                                    />
                                    <InputGroupAddon align="inline-end">
                                      <InputGroupButton
                                        onClick={() => {
                                          navigator.clipboard.readText().then((text) => {
                                            if (text) {
                                              field.onChange(text);
                                            }
                                          });
                                        }}
                                      >
                                        <ClipboardPasteIcon />
                                      </InputGroupButton>
                                    </InputGroupAddon>
                                  </InputGroup>
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <div className="flex items-start gap-2">
                  <RadioGroupItem value="stationary" id={`${radioId}-2`} aria-describedby={`${radioId}-2-description`} aria-controls={stationaryId} />
                  <div className="grow">
                    <div className="grid grow gap-2">
                      <Label htmlFor={`${radioId}-2`}>Stacjonarnie</Label>
                      <p id={`${radioId}-2-description`} className="text-text-tertiary text-xs">
                        Wybierz jeżeli chcesz aby spotkanie odbyło się w skazanej lokalizacji.
                      </p>
                    </div>
                    <div
                      role="region"
                      id={stationaryId}
                      aria-labelledby={`${radioId}-2`}
                      className="grid transition-all ease-in-out data-[state=collapsed]:grid-rows-[0fr] data-[state=collapsed]:opacity-0 data-[state=expanded]:grid-rows-[1fr] data-[state=expanded]:opacity-100"
                      data-state={type === 'stationary' ? 'expanded' : 'collapsed'}
                    >
                      <div className="pointer-events-none -m-2 overflow-hidden p-2">
                        <div className="pointer-events-auto mt-3">
                          <FormField
                            control={form.control}
                            name={locationName}
                            render={({ field }) => (
                              <FormItem className="flex flex-col">
                                <Popover>
                                  <PopoverTrigger asChild>
                                    <FormControl>
                                      <Button variant="outline" role="combobox" className={cn('h-13 min-h-13 w-full justify-start text-start cursor-pointer', !field.value && 'text-muted-foreground')}>
                                        {field.value ? (
                                          <>
                                            <MapPinIcon className="text-muted-foreground size-3.5" />
                                            <div className="flex flex-col overflow-hidden">
                                              <p>{field.value.name}</p>
                                              <p className="text-muted-foreground truncate text-xs">{field.value.address ?? ''}</p>
                                            </div>
                                          </>
                                        ) : (
                                          'Wybierz lokalizację'
                                        )}
                                        <ChevronsUpDownIcon className="text-muted-foreground ml-auto" />
                                      </Button>
                                    </FormControl>
                                  </PopoverTrigger>
                                  <PopoverContent className="max-w-sm min-w-xs p-0">
                                    <Command>
                                      <CommandInput placeholder="Wyszukaj lokację..." />
                                      <CommandList>
                                        <CommandEmpty>{isLocationsPending ? 'Ładowanie...' : 'Nie znaleziono lokacji.'}</CommandEmpty>
                                        <CommandGroup>
                                          {locations.map((location) => (
                                            <CommandItem value={String(location.id)} key={location.id} className="gap- flex h-11" onSelect={() => field.onChange(location)}>
                                              <MapPinIcon className="text-muted-foreground size-3.5" />
                                              <div className="flex flex-col overflow-hidden">
                                                <p>{location.name}</p>
                                                <p className="text-muted-foreground truncate text-xs">{location.address}</p>
                                              </div>
                                              <CheckIcon className={cn('ml-auto', location.id === Number(field.value?.id ?? 0) ? 'opacity-100' : 'opacity-0')} />
                                            </CommandItem>
                                          ))}
                                        </CommandGroup>
                                      </CommandList>
                                    </Command>
                                  </PopoverContent>
                                </Popover>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </RadioGroup>
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  );
}
